import classnames from 'classnames';
import { Toolbar } from 'primereact/toolbar';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
	WrappedButton,
	WrappedButtonType,
	WrappedSplitButton,
} from '../button';
import { WrappedInputText } from '../input';
import WrappedTooltip from '../overlay/WrappedTooltip.component';

const renderSearch = (
	showSearch,
	onSearchChange,
	onSearchExecute,
	searchInputPlaceholder,
	searchString,
	onKeyPressEnter
) =>
	showSearch ? (
		<div className="search-input">
			<div className="toolbar-search">
				<WrappedInputText
					className="ui-toolbar-search-input"
					onBlur={onSearchExecute}
					onChange={onSearchChange}
					onKeyPress={onKeyPressEnter}
					placeholder={searchInputPlaceholder}
					dataqa="searchbar"
					name="searchBar"
					value={searchString}
				/>
				<span
					className="pi pi-search"
					onClick={onSearchExecute}
					onKeyPress={onKeyPressEnter}
					tabIndex={0}
					role="button"
					aria-label="Search"
				/>
			</div>
		</div>
	) : null;

const renderButtons = (buttons, position, searchBar, extraContent) => {
	const filteredButtons = buttons.filter(
		({ group = 'left' }) => group === position
	);

	return (
		<div className={`p-toolbar-group-${position}`}>
			{!!searchBar && searchBar}
			<ul className="toolbar-list">
				{filteredButtons.map((params, i) => {
					const {
						label,
						icon,
						className,
						menuClassName,
						onClick = null,
						tooltip,
						tooltipOptions = { className: '' },
						disabled,
						model,
						showBadge = false,
						badgeCount = null,
						hide = false,
						buttonType = WrappedButtonType.TertiaryGrey,
						showUpgradeIcon = false,
						upgradePopoverContent,
						upgradePopoverActions,
					} = params;

					if (hide) {
						return null;
					}

					const btnClassName = classnames({
						'toolbar-split-button': model,
						'toolbar-button': !model,
						[className]: className,
					});

					const dataqaPrefix = !model
						? 'toolbar-button'
						: 'toolbar-split-button';
					const dataqa = params.dataqa
						? `${dataqaPrefix}-${params.dataqa}`
						: `${dataqaPrefix}-${label ? label.toLowerCase() : 'common'}`;

					const toolTipTarget = `toolbar-button-${className}-${position}-${i}`;
					const buttonProps = {
						buttonType,
						className: classnames(btnClassName, toolTipTarget),
						dataqa,
						disabled,
						icon,
						iconPos: params.iconPos,
						label,
						onClick,
						model,
						showUpgradeIcon,
						upgradePopoverContent,
						upgradePopoverActions,
					};

					const button = model ? (
						<WrappedSplitButton
							menuClassName={menuClassName}
							{...buttonProps}
							buttonType={buttonType}
						/>
					) : (
						<WrappedButton {...buttonProps} />
					);

					const badgeClassName = showBadge === true ? 'showBadgeItem' : '';

					return (
						<li
							className={`toolbar-item ${badgeClassName}`}
							key={i + (label || 'common')}
						>
							<div>
								{button}
								{tooltip && (
									<WrappedTooltip
										target={toolTipTarget}
										{...tooltipOptions}
										tooltip={tooltip}
									/>
								)}
								{showBadge && (
									<span
										data-qa={`toolbar-${params.dataqa}-count`}
										{...{ dataqa: `toolbar-${params.dataqa}-count` }}
										className="badge-icon"
									>
										{badgeCount}
									</span>
								)}
							</div>
						</li>
					);
				})}
			</ul>
			{extraContent}
		</div>
	);
};

export const WrappedToolbar = ({
	buttons = [],
	className,
	dataqa = 'common',
	searchCallback,
	searchInputPlaceholder,
	extraContent,
	showSearch,
}) => {
	const [searchString, setSearchString] = useState('');

	/**
	 * Updates the state with the search string, as typed into the search field.
	 */
	const onSearchChange = e => setSearchString(e.target.value);

	/**
	 * Event handler for search blur and search button click. Updates the grid filter based on search criteria.
	 */
	const onSearchExecute = () => {
		if (searchCallback) {
			searchCallback(searchString);
		}
	};

	/**
	 * Allows user to submit search by hitting CR from either the search field or when focussed on the search button.
	 */
	const onKeyPressEnter = e => {
		const { key } = e;

		if (key === 'Enter') {
			e.preventDefault();
			onSearchExecute();
		}
	};

	const searchBar = renderSearch(
		showSearch,
		onSearchChange,
		onSearchExecute,
		searchInputPlaceholder,
		searchString,
		onKeyPressEnter
	);

	const toolbarClassName = classnames('common-ui-toolbar', {
		[className]: className,
	});

	return (
		<div className={toolbarClassName} data-qa={`ui-toolbar-${dataqa}`}>
			<Toolbar
				left={renderButtons(buttons, 'left', searchBar, extraContent)}
				right={renderButtons(buttons, 'right')}
				dataqa={`ui-toolbar-${dataqa}`}
			/>
		</div>
	);
};

WrappedToolbar.propTypes = {
	buttons: PropTypes.arrayOf(
		PropTypes.shape({
			...WrappedSplitButton.propTypes,

			// placement of the button on the toolbar
			group: PropTypes.oneOf(['left', 'right']),
		})
	),
	className: PropTypes.string,
	dataqa: PropTypes.string,
	showSearch: PropTypes.bool,
	searchCallback: PropTypes.func,
	searchInputPlaceholder: PropTypes.string,
	extraContent: PropTypes.node,
};
